<!-- src/apps/secret/views/FileReceiveView.vue -->
<!-- secrets-files fork: descarga + descifrado de un SecretFile. -->

<script setup lang="ts">
  import { ref, computed, onMounted } from 'vue'
  import { useFileDownloader } from '@/shared/composables/useFileDownloader'
  import { useProductIdentity } from '@/shared/stores/identityStore'
  import BrandedHero from '@/apps/secret/components/layout/BrandedHero.vue'
  import type { FileMetadataResponse } from '@/api/files'

  const props = defineProps<{ fileIdentifier: string }>()

  const { primaryColor, cornerClass, buttonTextLight } = useProductIdentity()
  const { inspect, download, downloading, error } = useFileDownloader()
  const metadata = ref<FileMetadataResponse | null>(null)
  const passphrase = ref('')
  const passphraseFromFragment = ref(false)
  const downloadSucceeded = ref(false)
  const loading = ref(true)

  const buttonTextClass = computed(() => (buttonTextLight ? 'text-white' : 'text-gray-800'))

  onMounted(async () => {
    try {
      metadata.value = await inspect(props.fileIdentifier)
    } catch (ex) {
      const msg = ex instanceof Error ? ex.message : String(ex)
      // metadata queda null (404 = enlace ya usado / caducado / inexistente);
      // la UI muestra el estado "enlace no válido".
      console.warn('Failed to fetch metadata:', msg)
    } finally {
      loading.value = false
    }
    // Lee passphrase del fragmento de URL (#xxxx) sin enviarla al servidor
    const frag = window.location.hash.slice(1)
    if (frag) {
      passphrase.value = decodeURIComponent(frag)
      passphraseFromFragment.value = true
    }
  })

  async function onDownload() {
    try {
      await download(props.fileIdentifier, passphrase.value)
      downloadSucceeded.value = true
    } catch {
      // error captured in composable
    }
  }
</script>

<template>
  <div class="relative mx-auto w-full max-w-2xl px-4">
    <!-- Logo / wordmark + título -->
    <BrandedHero title="Recibir archivo cifrado" />

    <!-- Metadatos (solo si el enlace sigue siendo válido) -->
    <div
      v-if="metadata && metadata.state === 'new' && !downloadSucceeded"
      :class="[cornerClass]"
      class="border border-gray-200/50 bg-gradient-to-br from-white to-gray-50/30 p-8 text-sm shadow-[0_8px_30px_rgb(0,0,0,0.12),0_2px_8px_rgb(0,0,0,0.08)] backdrop-blur-sm dark:border-gray-700/50 dark:from-slate-900 dark:to-slate-800/30">
      <p class="text-gray-700 dark:text-gray-200"><strong>ID:</strong> {{ metadata.extid }}</p>
      <p class="text-gray-700 dark:text-gray-200">
        <strong>Tamaño cifrado:</strong> {{ (metadata.size_bytes / 1024).toFixed(1) }} KB
      </p>
      <p class="text-gray-700 dark:text-gray-200">
        <strong>Caduca:</strong>
        {{
          metadata.expires_at
            ? new Date(metadata.expires_at * 1000).toLocaleString()
            : '—'
        }}
      </p>
    </div>

    <!-- Enlace ya usado / caducado / inexistente -->
    <div
      v-if="!loading && !downloadSucceeded && (!metadata || metadata.state !== 'new')"
      :class="[cornerClass]"
      class="border border-gray-200/50 bg-gradient-to-br from-white to-gray-50/30 p-8 text-center shadow-[0_8px_30px_rgb(0,0,0,0.12),0_2px_8px_rgb(0,0,0,0.08)] backdrop-blur-sm dark:border-gray-700/50 dark:from-slate-900 dark:to-slate-800/30">
      <p class="font-medium text-gray-900 dark:text-white">Este enlace ya no es válido</p>
      <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
        El archivo ya fue descargado o el enlace ha caducado. Los enlaces de archivo son de un solo uso.
      </p>
    </div>

    <!-- Descarga -->
    <div
      v-if="metadata && metadata.state === 'new' && !downloadSucceeded"
      :class="[cornerClass]"
      class="mt-4 border border-gray-200/50 bg-gradient-to-br from-white to-gray-50/30 p-8 shadow-[0_8px_30px_rgb(0,0,0,0.12),0_2px_8px_rgb(0,0,0,0.08)] backdrop-blur-sm dark:border-gray-700/50 dark:from-slate-900 dark:to-slate-800/30">
      <label class="mb-1 block font-brand text-sm text-gray-600 dark:text-gray-300">Passphrase</label>
      <input
        v-model="passphrase"
        type="password"
        :class="[cornerClass]"
        class="w-full border border-gray-200/60 bg-white/80 px-5 py-2.5 text-sm text-gray-900 backdrop-blur-sm transition-all duration-300 hover:border-gray-300/80 focus:border-blue-500/80 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-500/20 dark:border-gray-700/60 dark:bg-slate-800/80 dark:text-white"
        :readonly="passphraseFromFragment"
        :placeholder="passphraseFromFragment ? '(en el fragmento del enlace)' : 'Pega la passphrase'" />

      <div class="mt-6 flex justify-end">
        <button
          type="button"
          :class="[cornerClass, buttonTextClass]"
          class="group relative flex w-full items-center justify-center gap-2.5 overflow-hidden px-5 py-3.5 text-lg font-semibold transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-offset-2 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto dark:focus:ring-offset-slate-900"
          :style="{
            backgroundColor: primaryColor,
            '--tw-ring-color': primaryColor,
            '--tw-ring-opacity': '0.2',
          }"
          :disabled="!passphrase || downloading"
          @click="onDownload">
          <span
            class="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100"
            aria-hidden="true"></span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            class="relative z-10 size-5">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          <span class="relative z-10">
            {{ downloading ? 'Descargando y descifrando…' : 'Descargar y descifrar' }}
          </span>
        </button>
      </div>

      <p v-if="error" class="mt-3 text-sm text-red-600 dark:text-red-400">{{ error }}</p>
    </div>

    <!-- Éxito -->
    <div
      v-if="downloadSucceeded"
      :class="[cornerClass]"
      class="mt-6 border border-gray-200/50 bg-gradient-to-br from-white to-gray-50/30 p-8 text-sm text-gray-700 shadow-[0_8px_30px_rgb(0,0,0,0.12),0_2px_8px_rgb(0,0,0,0.08)] backdrop-blur-sm dark:border-gray-700/50 dark:from-slate-900 dark:to-slate-800/30 dark:text-gray-200">
      Archivo descifrado y guardado. Este enlace ya no es válido.
    </div>
  </div>
</template>
